import { useCallback, useEffect, useRef, useState } from 'react';
import PartySocket from 'partysocket';
import { MP_HOST, loadToken, saveToken, loadNickname } from './mpConfig';

// ===== Online room connection (server-authoritative) =====
// This hook ONLY talks to the PartyKit room server. It never runs the game
// engine on the client: it renders the filtered `view` the server sends. The
// same engine.js remains the single source of rules (executed on the server).
//
// Step 2a scope: connect, claim/reclaim a seat (token), expose the current
// view and connection status. Move/nextHand actions are added in Step 2b via
// the returned `send` helper (protocol already supports them).
const OFFLINE_AFTER_MS = 10000;

// `createSize` (2/3/4) is sent only by the room creator's first hello; the
// server stores it and ignores it from anyone else.
export default function useScopaOnline(roomCode, createSize) {
  const [connected, setConnected] = useState(false);
  const [seat, setSeat] = useState(null);
  const [view, setView] = useState(null);
  const [error, setError] = useState(null);
  const [errorSeq, setErrorSeq] = useState(0); // bumps on every server error, even a repeated code
  // True once the server has been unreachable for OFFLINE_AFTER_MS (UI shows "Riprova").
  const [offline, setOffline] = useState(false);
  const socketRef = useRef(null);
  const tokenRef = useRef(null);
  const offlineTimer = useRef(null);
  const openRef = useRef(false);

  const armOfflineTimer = useCallback(() => {
    clearTimeout(offlineTimer.current);
    offlineTimer.current = setTimeout(() => setOffline(true), OFFLINE_AFTER_MS);
  }, []);

  // "Riprova": rebuild the socket from scratch (skips PartySocket's backoff wait).
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  const send = useCallback((obj) => {
    const s = socketRef.current;
    if (s && s.readyState === 1) {
      s.send(JSON.stringify(obj));
      return true;
    }
    return false;
  }, []);

  useEffect(() => {
    if (!roomCode || !MP_HOST) return undefined;
    tokenRef.current = loadToken(roomCode);
    const socket = new PartySocket({ host: MP_HOST, room: roomCode });
    socketRef.current = socket;
    openRef.current = false;
    setOffline(false);
    armOfflineTimer();

    const onOpen = () => {
      clearTimeout(offlineTimer.current);
      openRef.current = true;
      setOffline(false);
      setConnected(true);
      // hello (re)claims a seat; a stored token reclaims the same one. The
      // nickname is a public label the server associates to this seat.
      send({
        type: 'hello',
        token: tokenRef.current || undefined,
        nickname: loadNickname() || undefined,
        numPlayers: !tokenRef.current && createSize > 2 ? createSize : undefined,
      });
    };
    const onMessage = (e) => {
      let msg;
      try {
        msg = JSON.parse(e.data);
      } catch {
        return;
      }
      if (msg.type === 'hi') return;
      if (msg.type === 'welcome') {
        tokenRef.current = msg.token;
        saveToken(roomCode, msg.token);
        setSeat(msg.seat);
        if (msg.view) setView(msg.view);
        setError(null);
        return;
      }
      if (msg.type === 'state') {
        setView(msg);
        return;
      }
      if (msg.type === 'error') {
        setError(msg.code);
        setErrorSeq((n) => n + 1);
        if (msg.view) setView(msg.view);
      }
    };
    // Start the offline countdown only on the open->closed transition, so
    // repeated failed retries don't keep pushing the "Riprova" message away.
    const onDrop = () => {
      if (openRef.current) armOfflineTimer();
      openRef.current = false;
      setConnected(false);
    };
    const onClose = onDrop;
    const onError = onDrop;

    socket.addEventListener('open', onOpen);
    socket.addEventListener('message', onMessage);
    socket.addEventListener('close', onClose);
    socket.addEventListener('error', onError);

    return () => {
      socket.removeEventListener('open', onOpen);
      socket.removeEventListener('message', onMessage);
      socket.removeEventListener('close', onClose);
      socket.removeEventListener('error', onError);
      clearTimeout(offlineTimer.current);
      socket.close();
    };
  }, [roomCode, send, armOfflineTimer, attempt]); // eslint-disable-line react-hooks/exhaustive-deps

  // iOS Safari drops the socket in background: PartySocket auto-reconnects, and
  // on foreground we also request a fresh full view so the client never stays
  // on stale state. The client is never the source of truth.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && tokenRef.current) {
        send({ type: 'sync', token: tokenRef.current });
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [send]);

  return { connected, seat, view, error, errorSeq, send, tokenRef, offline, retry };
}
